import { yupResolver } from '@hookform/resolvers/yup';
import { array, boolean, date, number, object, string } from 'yup';
import { validateZip } from '~/ui/configs/form/validation';
import {
  hoursInvalid,
  minutesInvalid,
  requiredField,
  zipCodeInvalid,
} from '~/ui/constants/errorMessages';
import { TestingStatus } from '~/ui/pages/Visits/constants/toxicologyStatus';
import getSeconds from '~/ui/pages/Visits/utils/getSeconds';

export const NOTE_MAX_LENGTH = 20000;

const MIN_DURATION_SECONDS = 60;
const MAX_DURATION_SECONDS = 8 * 60 * 60;

const isWholeNumber = (value = ''): boolean => /^\d*$/.test(value.trim());

const isMinutesPart = (value = ''): boolean => isWholeNumber(value) && Number(value || 0) < 60;

const isManualAddressRequired = (type: number, address: number): boolean =>
  (type === 0 || type === 1) && !address;

const validationSchema = object().shape({
  date: date().required(requiredField),
  type: number().required(requiredField),
  visitedPeople: array().when('clientNoShow', {
    is: true,
    then: array().nullable(),
    otherwise: array().of(number()).required(requiredField).min(1, requiredField),
  }),
  note: string().max(
    NOTE_MAX_LENGTH,
    ({ value }) =>
      `You have reached the limit ${(
        value?.length || 0
      ).toLocaleString()} / ${NOTE_MAX_LENGTH.toLocaleString()}`,
  ),
  visitors: array().of(number()).required(requiredField).min(1, requiredField),
  startTime: date().required(requiredField).typeError(requiredField),
  toxicology: object().when('clientNoShow', {
    is: true,
    then: object().nullable(),
    otherwise: object()
      .shape({
        status: string().nullable(),
        result: string().when('status', {
          is: (value: TestingStatus) => value !== TestingStatus.DONE,
          then: string().nullable(),
          otherwise: string().required(requiredField).typeError(requiredField),
        }),
        date: string().when('status', {
          is: (value: TestingStatus) => value !== TestingStatus.DONE,
          then: string().nullable(),
          otherwise: string().required(requiredField).typeError(requiredField),
        }),
      })
      .nullable()
      .default(null),
  }),
  bloodDrawn: object().when('clientNoShow', {
    is: true,
    then: object().nullable(),
    otherwise: object()
      .shape({
        status: string().nullable(),
        drawDate: string().when('status', {
          is: (value: TestingStatus) => value !== TestingStatus.DONE,
          then: string().nullable(),
          otherwise: string().required(requiredField).typeError(requiredField),
        }),
      })
      .nullable()
      .default(null),
  }),
  durationHours: string().when('clientNoShow', {
    is: true,
    then: string().nullable(),
    otherwise: string().test('duration', (hours = '', { parent, createError }) => {
      const minutes: string = parent.durationMinutes ?? '';
      if (!hours.trim() && !minutes.trim()) return createError({ message: requiredField });
      if (!isWholeNumber(hours)) return createError({ message: hoursInvalid });
      if (!isMinutesPart(minutes)) return true;
      if (getSeconds(hours, minutes) > MAX_DURATION_SECONDS) {
        return createError({ message: 'Maximum 8 hours' });
      }
      return true;
    }),
  }),
  durationMinutes: string().when('clientNoShow', {
    is: true,
    then: string().nullable(),
    otherwise: string().test('minutes', (minutes = '', { parent, createError }) => {
      const hours: string = parent.durationHours ?? '';
      if (!isMinutesPart(minutes)) return createError({ message: minutesInvalid });
      if (!hours.trim() && !minutes.trim()) return true;
      if (!isWholeNumber(hours)) return true;
      if (getSeconds(hours, minutes) < MIN_DURATION_SECONDS) {
        return createError({ message: 'Minimum 1 minute' });
      }
      return true;
    }),
  }),
  clientNoShow: boolean().nullable(),
  noShowReason: string().when('clientNoShow', {
    is: true,
    then: string().required(requiredField).min(1, requiredField).typeError(requiredField),
    otherwise: string().nullable(),
  }),
  city: string().when(['type', 'address'], {
    is: (type: number, address: number) => isManualAddressRequired(type, address),
    then: string().required(requiredField),
    otherwise: string().nullable(),
  }),
  stateId: number().when(['type', 'address'], {
    is: (type: number, address: number) => isManualAddressRequired(type, address),
    then: number().required(requiredField),
    otherwise: number().nullable(),
  }),
  zipCode: string().when(['type', 'address'], {
    is: (type: number, address: number) => isManualAddressRequired(type, address),
    then: string().required(requiredField).test('length', zipCodeInvalid, validateZip),
    otherwise: string().nullable(),
  }),
  line1: string().when(['type', 'address'], {
    is: (type: number, address: number) => isManualAddressRequired(type, address),
    then: string().required(requiredField),
    otherwise: string().nullable(),
  }),
});

export default yupResolver(validationSchema);
